import React from "react";
import CorrespondencesLink from "./CorrespondencesLink";

// A row renders only when it has a value. That is what lets a tradition with no
// tarot and no planets simply not show those rows, with no conditional in the
// component — an empty "Tarot: —" would imply the tradition has one and just
// does not know it.
function Row({ label, value, dir, lang }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="detail__row">
      <dt className="detail__term">{label}</dt>
      <dd className="detail__value" dir={dir} lang={lang}>
        {value}
      </dd>
    </div>
  );
}

function Swatch({ label, name, hex, quadrants }) {
  if (!name && !hex) return null;
  const quartered = quadrants && quadrants.length === 4;
  return (
    <div className="detail__row">
      <dt className="detail__term">{label}</dt>
      <dd className="detail__value detail__value--color">
        {quartered ? (
          <span className="swatch swatch--quartered" aria-hidden="true">
            {quadrants.map((color, i) => (
              <span key={i} style={{ background: color }} />
            ))}
          </span>
        ) : (
          <span className="swatch" style={{ background: hex }} aria-hidden="true" />
        )}
        <span>
          {name} <span className="detail__hex">{hex}</span>
        </span>
      </dd>
    </div>
  );
}

// The planetary attribution, with its glyph and — where the attribution names a
// planet this application has a reference page for — a link to it.
//
// Three of the ten resolve to no link on purpose. Kether's Primum Mobile,
// Chokmah's Zodiac and Malkuth's sphere of the Elements sit in the same column
// of the tables and are not planets; Earth carries a glyph and has no page.
// Rendering a dead link for those would claim a page that does not exist.
function PlanetValue({ sephirah }) {
  if (!sephirah.planet) return null;
  const glyph = sephirah.planetSymbol ? (
    <span className="astro-glyph" aria-hidden="true">
      {`${sephirah.planetSymbol}\uFE0E `}
    </span>
  ) : null;

  if (!sephirah.planetPath) {
    return (
      <>
        {glyph}
        {sephirah.planet}
      </>
    );
  }

  return (
    <a className="planet-link" href={sephirah.planetPath} data-testid="planet-link">
      {glyph}
      {sephirah.planet}
    </a>
  );
}

// The alchemical glyphs for Air, Water and Fire are missing from a lot of
// system fonts and render as tofu. The data carries the glyph; the component
// decides whether to trust it, and for the three elements it falls back to the
// word rather than inventing a shape.
export function attributionLabel(path) {
  if (path.attributionType === "element" || !path.attributionGlyph) {
    return path.attribution;
  }
  return `${path.attributionGlyph} ${path.attribution}`;
}

// The commentary panel is shared: selecting a node and choosing a lens both
// write to this surface. The lens explainer shows when nothing is selected.
function LensCommentary({ lenses, sequence }) {
  if (lenses.length === 0) return null;

  return (
    <div className="commentary" data-testid="lens-commentary">
      {lenses.map((overlay) => (
        <section className="commentary__lens" key={overlay.slug}>
          <h2 className="commentary__name">{overlay.name}</h2>
          <p className="commentary__summary">{overlay.summary}</p>
          {overlay.body &&
            overlay.body
              .split(/\n\s*\n/)
              .map((paragraph) => paragraph.trim())
              .filter(Boolean)
              .map((paragraph, i) => (
                <p className="commentary__body" key={i}>
                  {paragraph}
                </p>
              ))}
          {sequence && sequence.overlay.slug === overlay.slug && sequence.current && (
            <p className="commentary__step" data-testid="commentary-step">
              <span className="commentary__step-index">
                {sequence.step} of {sequence.total}
              </span>{" "}
              {sequence.current.label}
            </p>
          )}
        </section>
      ))}
    </div>
  );
}

function EmptyPanel({ counts }) {
  // "joined by 0 paths" reads as broken rather than as deliberate, and a
  // tradition with no paths must not invite you to choose one.
  const hasPaths = counts.paths > 0;
  return (
    <aside className="tree__detail detail detail--empty">
      <p className="detail__prompt">
        {hasPaths
          ? `Choose a sphere or a path to read about it. This Tree has ${counts.sephiroth} sephiroth, joined by ${counts.paths} paths.`
          : `Choose a node to read about it. This Tree has ${counts.sephiroth} nodes, ${counts.counted} of them counted among the ten.`}
      </p>
    </aside>
  );
}

function SephirahDetail({ sephirah, tradition }) {
  // The four Golden Dawn scales are Hermetic vocabulary. Where a tradition has
  // none, its fill is UI chrome and must not be listed as a correspondence.
  const colourIsDoctrine = sephirah.colorIsTraditional !== false;
  const counted = sephirah.countedInTen !== false;

  return (
    <>
      <header className="detail__header">
        {sephirah.number !== null && sephirah.number !== undefined ? (
          <p className="detail__number">{sephirah.number}</p>
        ) : (
          <p className="detail__number detail__number--none" aria-hidden="true">
            &mdash;
          </p>
        )}
        <h2 className="detail__name">{sephirah.name}</h2>
        <p className="detail__title">{sephirah.title}</p>
        <p className="detail__hebrew" dir="rtl" lang="he">
          {sephirah.hebrew}
        </p>
      </header>

      {!counted && (
        <p className="detail__caveat">
          Drawn, but not counted among the ten.
        </p>
      )}

      <p className="detail__summary">{sephirah.summary}</p>

      <dl className="detail__list">
        <Row label="Planet" value={sephirah.planet && <PlanetValue sephirah={sephirah} />} />
        <Row label="Sphere" value={sephirah.planetHebrew} />
        <Row label="Note" value={sephirah.planetNote} />
        {/* All four of 777's scales, in the order the book prints them, and
            only where this tradition's colour is doctrine rather than chrome.
            The Queen one alone can be quartered — Malkuth's four elemental
            quarters are printed in that column and nowhere else. */}
        {colourIsDoctrine && (
          <Swatch label="King scale" name={sephirah.colorKing} hex={sephirah.colorKingHex} />
        )}
        {colourIsDoctrine && (
          <Swatch
            label="Queen scale"
            name={sephirah.colorQueen}
            hex={sephirah.colorQueenHex}
            quadrants={sephirah.colorQueenQuadrants}
          />
        )}
        {colourIsDoctrine && (
          <Swatch label="Prince scale" name={sephirah.colorPrince} hex={sephirah.colorPrinceHex} />
        )}
        {colourIsDoctrine && (
          <Swatch
            label="Princess scale"
            name={sephirah.colorPrincess}
            hex={sephirah.colorPrincessHex}
          />
        )}
        <Row label="Pillar" value={sephirah.pillar} />
        <Row label="Divine name" value={sephirah.divineName} />
        <Row label="In Hebrew" value={sephirah.divineNameHebrew} dir="rtl" lang="he" />
        <Row
          label="Archangel"
          value={
            sephirah.archangelPath ? (
              <a className="archangel-link" href={sephirah.archangelPath}>
                {sephirah.archangel}
              </a>
            ) : (
              sephirah.archangel
            )
          }
        />
        <Row label="Angelic choir" value={sephirah.angelicChoir} />
        <Row label="Partzuf" value={sephirah.partzuf} />
        <Row label="World" value={sephirah.world} />
        <Row label="Soul level" value={sephirah.soulLevel} />
        <Row label="Shepherd" value={sephirah.shepherd} />
        <Row label="Day of creation" value={sephirah.dayOfCreation} />
        <Row label="Omer week" value={sephirah.omerWeek} />
        <Row
          label="Tarot"
          value={
            sephirah.tarotNumberPath ? (
              <a className="tarot-card-link" href={sephirah.tarotNumberPath}>
                {sephirah.tarot}
              </a>
            ) : (
              sephirah.tarot
            )
          }
        />
        <Row label="Magical image" value={sephirah.magicalImage} />
        <Row label="Virtue" value={sephirah.virtue} />
        <Row label="Vice" value={sephirah.vice} />
        <Row label="Body" value={sephirah.bodyPart} />
      </dl>

      <CorrespondencesLink node={sephirah} tradition={tradition} type="sephirah" />
    </>
  );
}

function PathDetail({ path, tradition }) {
  const trump =
    path.tarot && path.tarotNumber !== null && path.tarotNumber !== undefined
      ? `${path.tarot} (${path.tarotNumber})`
      : path.tarot;

  const veils = [
    path.crossesAbyss ? "Crosses the Abyss" : null,
    path.crossesParoketh ? "Pierces the Veil of Paroketh" : null
  ]
    .filter(Boolean)
    .join(". ");

  return (
    <>
      <header className="detail__header">
        <p className="detail__number">{path.number}</p>
        <h2 className="detail__name">{path.letterName}</h2>
        <p className="detail__title">{path.letterMeaning}</p>
        <p className="detail__hebrew" dir="rtl" lang="he">
          {path.hebrewLetter}
        </p>
      </header>

      <p className="detail__summary">{path.summary}</p>

      <dl className="detail__list">
        <Row label="Joins" value={`${path.fromName} to ${path.toName}`} />
        <Row label="Gematria" value={path.letterValue} />
        <Row label="Letter class" value={path.letterClass} />
        <Row label="Yetziratic title" value={path.yetziraticTitle} />
        <Row
          label="Tarot trump"
          value={
            path.tarotPath ? (
              <a className="tarot-card-link" href={path.tarotPath}>{trump}</a>
            ) : (
              trump
            )
          }
        />
        <Row label={path.attributionType} value={attributionLabel(path)} />
        <Swatch label="King scale" name={path.colorKing} hex={path.colorKingHex} />
        <Swatch label="Queen scale" name={path.colorQueen} hex={path.colorQueenHex} />
        <Swatch label="Prince scale" name={path.colorPrince} hex={path.colorPrinceHex} />
        <Swatch label="Princess scale" name={path.colorPrincess} hex={path.colorPrincessHex} />
        <Row label="Orientation" value={path.orientation} />
        <Row label="Pillar" value={path.pillar} />
        <Row label="Veils" value={veils} />
      </dl>

      <CorrespondencesLink node={path} tradition={tradition} type="path" />
    </>
  );
}

// AN OVERLAY GROUP: a marker rule, a negative veil, or a band of a region lens.
// All three are things drawn on the Tree that are not nodes of it, so all three
// arrive here through the one selection value.
//
// Same shape as the other two details — a
// header, a summary, declared rows, then the way through to the sources — but
// its commentary is prose rather than correspondences, because that is what the
// material is. Nothing here is invented when a field is empty: a marker with no
// Hebrew name and no translation simply shows neither row.
function MarkerDetail({ group }) {
  const paragraphs = (group.body || "")
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return (
    <>
      <header className="detail__header">
        <p className="detail__number detail__number--none" aria-hidden="true">
          &mdash;
        </p>
        <h2 className="detail__name">{group.name}</h2>
        {group.translation && <p className="detail__title">{group.translation}</p>}
        {group.hebrew && (
          <p className="detail__hebrew" dir="rtl" lang="he">
            {group.hebrew}
          </p>
        )}
      </header>

      <p className="detail__caveat">
        {group.channel === "region"
          ? "A region of the Tree, not one of its nodes."
          : "Drawn on the Tree, but not one of its nodes."}
      </p>

      {group.note && <p className="detail__summary">{group.note}</p>}

      {paragraphs.map((paragraph, i) => (
        <p className="detail__body" key={i}>
          {paragraph}
        </p>
      ))}

      {group.researchPath && (
        <section className="detail-link" data-testid="marker-sources">
          <h3 className="detail-link__heading">Sources</h3>
          <p className="detail-link__body">
            Every claim above, with the edition and locator it came from.
          </p>
          <a
            className="detail-link__button"
            href={group.researchPath}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="marker-sources-link"
          >
            {`Sources for ${group.name}`}
          </a>
        </section>
      )}
    </>
  );
}

// One panel, switching on the single selection value's type.
export default function DetailPanel({ node, counts, tradition, lenses = [], sequence = null }) {
  if (!node) {
    if (lenses.length > 0) {
      return (
        <aside className="tree__detail detail" aria-live="polite" role="complementary">
          <LensCommentary lenses={lenses} sequence={sequence} />
        </aside>
      );
    }
    return <EmptyPanel counts={counts} />;
  }

  return (
    <aside className="tree__detail detail" aria-live="polite" role="complementary">
      {node.type === "sephirah" && (
        <SephirahDetail sephirah={node.data} tradition={tradition} />
      )}
      {node.type === "path" && <PathDetail path={node.data} tradition={tradition} />}
      {node.type === "marker" && <MarkerDetail group={node.data} />}
    </aside>
  );
}
